/**
 * Base do relatório PDF da lista de OS.
 *
 * Aqui fica apenas o MECANISMO (montagem da tabela pdfmake). As colunas são
 * declaradas por cada submódulo no seu próprio arquivo de relatório
 * (ex: `modulos/solo/meta/relatorio.ts`) — assim um tipo de coleta novo não
 * exige mexer neste arquivo nem acrescentar condicionais.
 */

/** Uma coluna do relatório: título no cabeçalho + como extrair o valor da OS. */
export interface ColunaRelatorio<T> {
  titulo: string
  /** Extrai o texto da célula. Deve devolver string — formate datas/números aqui. */
  valor: (item: T) => string
  /** Largura no pdfmake ('*' distribui igualmente). @default '*' */
  largura?: string | number
}

/** Estilo das células do corpo, igual ao da versão em produção. */
const CELULA = { fontSize: 7, margin: [0, 2, 0, 2] as number[], alignment: 'center' }
/** Estilo das células do cabeçalho. */
const CABECALHO = { style: 'tableHeader', fontSize: 9, alignment: 'center' }

/** Zebra as linhas — mesma alternância de cor do relatório legado. */
function zebrar(rowIndex: number) {
  return rowIndex % 2 === 0 ? '#f5f5f5' : null
}

/**
 * Monta a tabela principal do relatório no formato que o `GeneratePdfButton`
 * (Components/relatorio-component) espera receber em `dados`.
 */
export function montarTabelaRelatorio<T>(lista: T[], colunas: ColunaRelatorio<T>[]) {
  const corpo = (lista ?? []).map((item) =>
    colunas.map((coluna) => ({ text: coluna.valor(item) ?? '', ...CELULA }))
  )

  return [
    {
      table: {
        headerRows: 1,
        widths: colunas.map((coluna) => coluna.largura ?? '*'),
        body: [colunas.map((coluna) => ({ text: coluna.titulo, ...CABECALHO })), ...corpo],
        dontBreakRows: false,
        keepWithHeaderRows: 0,
      },
      layout: { fillColor: zebrar },
    },
  ]
}

/**
 * Monta o bloco de totais exibido ao fim do relatório (prop `totais`).
 * `rotulo` permite ao submódulo nomear o que está sendo contado.
 */
export function montarTotaisRelatorio(total: number, rotulo = 'Total OS') {
  return [
    {
      table: {
        headerRows: 1,
        widths: [95],
        body: [
          [{ text: rotulo, ...CABECALHO }],
          [{ text: String(total ?? 0), ...CELULA }],
        ],
        margin: [0, 50, 0, 0],
        alignment: 'center',
      },
      layout: { fillColor: zebrar },
    },
  ]
}
